import API from './ApiBase';
import type {
  SignupRequest,
  SignupResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  VerifyEmailResponse,
  ResetPassswordRequest,
  VerifyResetTokenResponse,
  VerifyResetTokenRequest,
  ResendVerifyEmailResponse
} from '../types/Auth.types';

export const authService = {
  async signup(data: SignupRequest): Promise<SignupResponse> {
    try {
      const response = await API.post<SignupResponse>(
        '/auth/signup/init',
        data
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create account. Please try again.');
    }
  },

  async verifyEmail(token: string): Promise<VerifyEmailResponse> {
    try {
      const response = await API.post<VerifyEmailResponse>(
        `/auth/verify-email/${encodeURIComponent(token)}`
      );
      return response.data;
    } catch (error: any) {
      const verifyError = new Error(
        error?.response?.data?.message ||
          'Verification failed. Please try again or request a new verification email.'
      ) as Error & { status?: number };

      verifyError.status = error?.response?.status;

      throw verifyError;
    }
  },

  async resendVerifyEmail(token: string): Promise<ResendVerifyEmailResponse> {
    try {
      const response = await API.post<ResendVerifyEmailResponse>(
        '/auth/email/resend-expired',
        { token }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          'Failed to resend verification email. Please try again.'
      );
    }
  },

  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await API.post<LoginResponse>('/auth/login', data);
      if (response.status !== 201) {
        throw new Error(response.data.message || 'Login failed');
      }
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.message) {
        throw error;
      }
      throw new Error('Login failed. Please try again.');
    }
  },

  async logout(): Promise<LogoutResponse> {
    try {
      const response = await API.post<LogoutResponse>('/auth/logout');
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Logout failed. Please try again.');
    }
  },

  async refreshToken(): Promise<boolean> {
    try {
      const response = await API.post('/auth/refresh');
      return response.status === 201;
    } catch (error: any) {
      return false;
    }
  },

  async forgotPassword(email: string): Promise<void> {
    try {
      await API.post('/auth/forgot-password', { email });
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          'Failed to send reset password email. Please try again.'
      );
    }
  },

  async verifyResetToken(data: VerifyResetTokenRequest) {
    try {
      const response = await API.post<VerifyResetTokenResponse>(
        '/auth/verify-reset-token',
        data
      );
      return response.data;
    } catch (error: any) {
      const verifyError = new Error(
        error?.response?.data?.message ||
          'Invalid or expired reset token. Please request a new password reset.'
      ) as Error & { status?: number };

      verifyError.status = error?.response?.status;

      throw verifyError;
    }
  },

  async resetPassword(data: ResetPassswordRequest): Promise<void> {
    try {
      await API.post('/auth/reset-password', data);
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          'Failed to send reset password request. Please try again.'
      );
    }
  }
};
